'use client';

import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, X, Spinner, MagnifyingGlass } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useAddressStore } from '@/stores/address-store';
import type { AddressSuggestion } from '@/lib/api/delivery';

interface AddressInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export function AddressInput({
  value: controlledValue,
  onChange,
  onSelect,
  placeholder = 'Введите адрес доставки',
  className,
  autoFocus = false,
}: AddressInputProps) {
  const [internalValue, setInternalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Повтор подсказок идёт в обход store-шного isLoading (его ставит только
  // checkAddress). Без своего флага кнопка «Повторить» выглядит мёртвой.
  const [isRetrying, setIsRetrying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const value = controlledValue ?? internalValue;
  const setValue = (newValue: string) => {
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  const {
    suggestions,
    suggestError,
    isLoading,
    selectedCity,
    fetchSuggestions,
    clearSuggestions,
    selectSuggestion,
  } = useAddressStore();

  // Handle input change with debounce
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API call
    debounceRef.current = setTimeout(() => {
      if (newValue.length >= 3) {
        setHasSearched(true);
        fetchSuggestions(newValue);
      } else {
        setHasSearched(false);
        clearSuggestions();
      }
    }, 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSuggestions, clearSuggestions]);

  // Handle suggestion click
  const handleSuggestionClick = useCallback(async (suggestion: AddressSuggestion) => {
    setValue(suggestion.value);
    setHasSearched(false);
    await selectSuggestion(suggestion);
    onSelect?.(suggestion);
    setIsFocused(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectSuggestion, onSelect]);

  // Повтор запроса подсказок после сбоя сервиса
  const handleRetrySuggestions = useCallback(async () => {
    setIsRetrying(true);
    try {
      await fetchSuggestions(value);
    } finally {
      setIsRetrying(false);
    }
  }, [fetchSuggestions, value]);

  // Handle clear
  const handleClear = useCallback(() => {
    setValue('');
    setHasSearched(false);
    clearSuggestions();
    inputRef.current?.focus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSuggestions]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const showSuggestions = isFocused && suggestions.length > 0;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {/* Input Container */}
      <div
        className={cn(
          'relative flex items-center gap-3',
          'h-14 px-4 rounded-card',
          'bg-[var(--surface-secondary)]',
          'border-2 transition-all duration-200',
          // Фирменный фиолетовый ореол фокуса СОХРАНЁН (ответ 35 = Б). Это не
          // системная глубина, а кольцо фокуса: 4px spread без размытия. Роль
          // shadow-glow сюда не подходит — она рисует размытый ореол 20px и
          // превратила бы чёткое кольцо в свечение.
          isFocused
            ? 'border-[var(--primary)] shadow-[0_0_0_4px_rgba(123,66,255,0.1)]'
            : 'border-transparent [@media(hover:hover)]:hover:border-[var(--border-default)]'
        )}
      >
        {/* Icon */}
        <div className="flex-shrink-0">
          {isLoading ? (
            <Spinner className="h-5 w-5 text-[var(--primary)] animate-spin" weight="bold" />
          ) : (
            <MagnifyingGlass className="h-5 w-5 text-[var(--text-tertiary)]" weight="bold" />
          )}
        </div>

        {/* Input - font-size 16px prevents auto-zoom on iOS */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          placeholder={selectedCity ? `Адрес в г. ${selectedCity.name}` : placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          className={cn(
            'flex-1 bg-transparent',
            'text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]',
            'text-base font-medium', // 16px prevents iOS auto-zoom
            'outline-none'
          )}
        />

        {/* Clear Button */}
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'flex-shrink-0 p-1.5 rounded-pill',
              'text-[var(--text-tertiary)]',
              '[@media(hover:hover)]:hover:text-[var(--text-primary)]',
              '[@media(hover:hover)]:hover:bg-[var(--surface-hover)]',
              'active:bg-[var(--surface-hover)] active:text-[var(--text-primary)]',
              'transition-colors duration-150'
            )}
          >
            <X className="h-4 w-4" weight="bold" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div
          className={cn(
            'absolute z-50 w-full mt-2',
            'bg-[var(--surface-primary)]',
            'border border-[var(--border-default)]',
            'rounded-card shadow-float overflow-hidden'
          )}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className={cn(
                'w-full flex items-start gap-3 p-4 text-left',
                '[@media(hover:hover)]:hover:bg-[var(--surface-secondary)]',
                'active:bg-[var(--surface-secondary)]',
                'transition-colors duration-150',
                index !== 0 && 'border-t border-[var(--border-default)]'
              )}
            >
              <MapPin className="h-5 w-5 text-[var(--primary)] flex-shrink-0 mt-0.5" weight="fill" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] line-clamp-2">
                  {suggestion.value}
                </p>
                {suggestion.data.city && suggestion.data.city !== selectedCity?.name && (
                  <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
                    {suggestion.data.city}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results / сбой подсказок */}
      {isFocused && hasSearched && value.length >= 3 && suggestions.length === 0 && !isLoading && (
        <div
          className={cn(
            'absolute z-50 w-full mt-2',
            'bg-[var(--surface-primary)]',
            'border border-[var(--border-default)]',
            'rounded-card shadow-float p-4'
          )}
        >
          {suggestError ? (
            // Пустой список после сбоя сервиса — не «адрес не найден».
            // Разные причины требуют разных действий, поэтому и текст разный.
            <div className="text-center">
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                Поиск адреса временно не работает
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Повторите попытку или укажите дом на карте.
              </p>
              <button
                type="button"
                onClick={handleRetrySuggestions}
                disabled={isRetrying}
                className={cn(
                  'mt-2.5 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-pill',
                  'border border-[var(--border-default)]',
                  'text-xs font-semibold text-[var(--text-primary)]',
                  '[@media(hover:hover)]:hover:bg-[var(--surface-secondary)]',
                  'transition-colors duration-150 disabled:opacity-60'
                )}
              >
                {isRetrying && <Spinner className="h-3.5 w-3.5 animate-spin" weight="bold" />}
                <span>{isRetrying ? 'Ищем…' : 'Повторить'}</span>
              </button>
            </div>
          ) : (
            <p className="text-sm text-[var(--text-secondary)] text-center">
              Адрес не найден. Попробуйте уточнить запрос.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

